import { useCallback, useState } from 'react';

import { useApolloClient } from '@apollo/client';

import { useSanityGqlEndpoint } from 'state/network';
import logger from 'utils/logger';

import {
  EngineOfferListItem,
  EnginePersonalizedOffer,
  isEnginePersonalizedOffer,
  isEnginePersonalizedOfferGroup,
} from '../types';

import {
  IPersonalizedData,
  fetchPersonalizedOfferData as fetchData,
  getOfferGroupCmsIds,
} from './utils/personalized-offers';

export type PersonalizedOffersFetchResult =
  | { status: 'not-required'; data: null }
  | { status: 'succeeded'; data: IPersonalizedData }
  | { status: 'failed'; data: null };

export const usePersonalizedOffers: () => [
  (offers: readonly EngineOfferListItem[]) => Promise<PersonalizedOffersFetchResult>,
  { loading: boolean }
] = () => {
  const [loading, setLoading] = useState(false);

  const client = useApolloClient();
  const uri = useSanityGqlEndpoint();

  const fetchPersonalizedOfferData = useCallback(
    async (offers: readonly EngineOfferListItem[]): Promise<PersonalizedOffersFetchResult> => {
      const standaloneConfigOffersIds = offers
        .filter(
          (offer): offer is EnginePersonalizedOffer =>
            isEnginePersonalizedOffer(offer) && !!offer.configId
        )
        .map(offer => offer.configId);

      const groupOfferConfigIds = offers
        .filter(isEnginePersonalizedOfferGroup)
        .flatMap(group => group.offers)
        .filter((offer): offer is EnginePersonalizedOffer => Boolean(offer?.configId))
        .map(offer => offer.configId);

      const offerGroupsIds = getOfferGroupCmsIds(offers, false);

      const configOffersIds = Array.from(new Set(standaloneConfigOffersIds));
      const systemwideOffersIds = Array.from(new Set(groupOfferConfigIds));

      if (!configOffersIds.length && !systemwideOffersIds.length && !offerGroupsIds.length) {
        return { status: 'not-required', data: null };
      }

      try {
        setLoading(true);
        const data = await fetchData({
          client,
          uri,
          configOffersIds,
          systemwideOffersIds,
          offerGroupsIds,
        });
        return { status: 'succeeded', data };
      } catch (e) {
        logger.error({ error: e, message: 'Error fetching personalized offers data' });
        return { status: 'failed', data: null };
      } finally {
        setLoading(false);
      }
    },
    [client, uri]
  );

  return [fetchPersonalizedOfferData, { loading }];
};
